import { useState } from 'react';
import { DataTable } from '@/components';
import { IconInput } from '@/components';
import { columns } from './Columns';
import { Search } from 'lucide-react';
import { Pagination, PaginationState } from '@/components';
import { useListProfiles } from '../hooks/profilesAPI';
import { useDebounce } from '@/utils';
import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ProfilesTableSkeleton } from './ProfilesTableSkeleton';

export const ProfilesTable = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 50,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 1000);

  // Split into terms and filter out empty strings after trim
  const searchTerms = debouncedSearch.trim()
    ? debouncedSearch
        .split(/\s+/)
        .map((term) => term.trim())
        .filter((term) => term.length > 0)
    : undefined;

  // Convert all sorting states to API format
  const sortColumns = sorting.map((sort) => ({
    column: sort.id,
    order: sort.desc ? ('desc' as const) : ('asc' as const),
  }));

  const { data: profiles, isLoading } = useListProfiles({
    searchTerms,
    sortColumns: sortColumns.length > 0 ? sortColumns : undefined,
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, page: 0 })); // Reset to first page on search
  };

  if (isLoading) {
    return <ProfilesTableSkeleton pageSize={pagination.pageSize} />;
  }

  return (
    <div className="container mx-auto p-10">
      <div className="mb-4 w-1/3">
        <IconInput
          startIcon={<Search className="text-muted-foreground h-4 w-4" />}
          placeholder="Search Profiles..."
          value={searchQuery}
          onChange={handleSearch}
        />
      </div>
      <div className="relative">
        <DataTable
          columns={columns}
          data={profiles?.data ?? []}
          sorting={sorting}
          onSortingChange={setSorting as OnChangeFn<SortingState>}
        />
      </div>
      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        totalCount={profiles?.totalCount ?? 0}
        pageSizeOptions={[50, 75, 100]}
      />
    </div>
  );
};
